import React, { useEffect, useRef } from "react"
import { Form, Button } from "reactstrap"
import Select from "react-select"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import { useTranslation } from "react-i18next"

const SELECT_MENU_PORTAL_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
}

const withSelectMenuLayering = styles => ({
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
  ...styles,
})

const ItemFilter = ({
  filters,
  searchTerm,
  handleSearchChange,
  handleFilterChange,
  handleSearch,
  onReset = () => {},
  selectedFilters,
  isRangeSlider,
  priceRange,
  setPriceRange,
  hideSearch,
  isRangeDate,
  dateRange,
  searchBy,
  setDateRange,
  isCountrySelected = true,
}) => {
  const { t } = useTranslation()
  const prevSearchTermRef = useRef("")

  const getSelectNoOptionsMessage = filter => {
    if (filter.dependsOnCountry && !isCountrySelected) {
      return t("Please select country first")
    }
    return t("No options")
  }

  const resolveSelectOptions = filter => {
    if (filter.dependsOnCountry && !isCountrySelected) {
      return []
    }
    return filter.options || []
  }
  const handleSearchClick = () => {
    handleSearch()
  }

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleSearch() // Trigger search
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleSearch])

  useEffect(() => {
    if (prevSearchTermRef.current !== "" && searchTerm.trim() === "") {
      handleSearch() // Trigger search only when searchTerm is cleared
    }

    prevSearchTermRef.current = searchTerm // Update previous value
  }, [searchTerm])

  return (
    <Form>
      <div
        className="mb-3 pb-3 border-bottom portal-filter-bar"
        data-col-size={4}
      >
        {hideSearch ? null : (
          <div className="portal-filter-bar__item">
            <div className="filter-search-field">
              <i className="bx bx-search-alt-2 filter-search-icon" aria-hidden="true" />
              <input
                name="search"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchBy ? searchBy : t("Keyword")}
                title={searchBy ? searchBy : t("Keyword")}
                className="form-control filter-search-input"
              />
            </div>
          </div>
        )}
        {filters.map((filter, index) => (
          <div key={index} className="portal-filter-bar__item">
            {filter.type === "select" && (
              <select
                className="form-select"
                value={selectedFilters[filter.name]}
                onChange={e => handleFilterChange(e.target.value, filter.name)}
              >
                <option hidden>{filter.placeholder}</option>
                {filter.options.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {filter.type === "multiSelect" && (
              <Select
                value={selectedFilters[filter.name]}
                isMulti={true}
                onChange={selectedOption => {
                  handleFilterChange(selectedOption, filter.name)
                }}
                options={resolveSelectOptions(filter)}
                placeholder={filter.placeholder}
                noOptionsMessage={() => getSelectNoOptionsMessage(filter)}
                className="select2-selection"
                closeMenuOnSelect={false}
                {...SELECT_MENU_PORTAL_PROPS}
                styles={withSelectMenuLayering({
                  valueContainer: provided => ({
                    ...provided,
                    display: "flex",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    maxHeight: "40px",
                    scrollbarWidth: "thin",
                  }),
                  multiValue: provided => ({
                    ...provided,
                    flex: "0 0 auto",
                    backgroundColor: "#556EE6",
                  }),
                  multiValueLabel: provided => ({
                    ...provided,
                    color: "white",
                  }),
                  multiValueRemove: provided => ({
                    ...provided,
                    color: "white",
                    ":hover": {
                      backgroundColor: "#FFBDAD",
                      color: "#DE350B",
                    },
                  }),
                  control: provided => ({
                    ...provided,
                    minHeight: "40px",
                    height: "40px",
                  }),
                  indicatorsContainer: provided => ({
                    ...provided,
                    height: "40px",
                  }),
                  option: provided => ({
                    ...provided,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }),
                })}
              />
            )}
            {filter.type === "singleSelect" && (
              <Select
                value={selectedFilters[filter.name]}
                isMulti={false}
                onChange={selectedOption => {
                  handleFilterChange(selectedOption, filter.name)
                }}
                options={resolveSelectOptions(filter)}
                placeholder={filter.placeholder}
                noOptionsMessage={() => getSelectNoOptionsMessage(filter)}
                className="select2-selection"
                {...SELECT_MENU_PORTAL_PROPS}
                styles={withSelectMenuLayering({
                  option: provided => ({
                    ...provided,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }),
                })}
              />
            )}
          </div>
        ))}
        {isRangeDate && (
          <div className="portal-filter-bar__item">
            <Flatpickr
              className="form-control d-block"
              placeholder="YYYY-MM-DD"
              options={{
                mode: "range",
                dateFormat: "Y-m-d",
                // defaultDate: [new Date("2024-05-01"), new Date("2024-05-21")],
              }}
              onChange={selectedDates => {
                const formattedDates = selectedDates.map(
                  date =>
                    date
                      .toLocaleDateString("en-CA", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      .split("/")
                      .reverse()
                      .join("-") // Format to "yyyy-mm-dd"
                )
                const minDate = formattedDates[0]
                const maxDate = formattedDates[1]
                setDateRange({
                  min: minDate,
                  max: maxDate,
                })
              }}
            />
          </div>
        )}

        {isRangeSlider && (
          <div className="portal-filter-bar__item">
            <div className="portal-filter-range">
              <input
                value={priceRange[0]}
                onChange={e => {
                  const newValue = parseInt(e.target.value)
                  setPriceRange([newValue, priceRange[1]])
                }}
                className="form-control"
                type="number"
                placeholder={t("Min Price")}
                title={t("Min Price")}
              />
              <input
                value={priceRange[1]}
                type="number"
                onChange={e => {
                  const newValue = parseInt(e.target.value)
                  setPriceRange([priceRange[0], newValue])
                }}
                className="form-control"
                placeholder={t("Max Price")}
                title={t("Max Price")}
              />
            </div>
          </div>
        )}

        <div className="portal-filter-bar__actions">
          <div className="d-flex gap-2">
            <Button
              color="success"
              onClick={handleSearchClick}
              className="g_center d-flex portal-filter-bar__btn"
              style={{ minWidth: 40, height: 39, padding: "0 10px" }}
            >
              <i className="bx bx-search-alt-2" style={{ fontSize: 22 }}></i>{" "}
              {t("Search")}
            </Button>
            <Button
              color="success"
              onClick={onReset}
              className="g_center d-flex btn-light portal-filter-bar__btn"
              style={{ minWidth: 40, height: 39, padding: "0 10px" }}
            >
              <i className="mdi mdi-refresh me-1" style={{ fontSize: 22 }}></i>{" "}
              {t("Reset")}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  )
}

export default ItemFilter
